import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { FilterChip } from "@volca/ui/components/ui/filter-chip";
import { MultiSelect } from "@volca/ui/components/ui/multi-select";
import { Stack } from "@volca/ui/components/ui/stack";
import { useReportCurrency } from "@/profitability/currency";
import type {
  ReportControl,
  ReportControlValues,
  ReportDataset,
  ReportValue,
} from "@/profitability/types";
import { FilterChoice } from "./filter-choice";
import { FilterValue } from "./filter-value";
import { columnLabel, formatReportValue } from "./utils";

const COMPARISON = {
  gte: "at least",
  lte: "at most",
  contains: "contains",
} as const;

const INPUT_TYPE = {
  number: "number",
  currency: "number",
  percent: "number",
  date: "date",
  text: "text",
  boolean: "text",
} as const;

const BOOLEAN_CHOICES = [
  { id: "true", name: "Yes" },
  { id: "false", name: "No" },
];

export const ReportControls = ({
  controls,
  datasets,
  choices,
  values,
  onChange,
  before,
}: {
  controls: ReportControl[];
  datasets: ReportDataset[];
  /** What each `in` and `eq` filter offers, from `controlChoices`. */
  choices: Record<string, ReportValue[]>;
  /** Already narrowed to those choices, by `currentValues`. */
  values: ReportControlValues;
  onChange: (values: ReportControlValues) => void;
  /** Shown first in the row, for a control the script did not declare. */
  before?: ReactNode;
}) => {
  const currency = useReportCurrency();
  // Where a saved value sits among the choices. A value that is no longer one
  // of them reads as no selection: as "-1" it would match no option, and the
  // control would show nothing chosen while still narrowing the page.
  const choiceIndex = (id: string, value: ReportValue) => {
    const index = choices[id].indexOf(value);
    return index === -1 ? undefined : String(index);
  };
  const setValue = (
    id: string,
    value: ReportValue | ReportValue[] | undefined,
  ) => {
    const next = { ...values };
    if (value === undefined) delete next[id];
    else next[id] = value;
    onChange(next);
  };
  if (!controls.length && !before) return null;
  // Filters on different tables can share a field ("Department" on the jobs,
  // the technicians and the employees): a shared name says which table each
  // one filters.
  const named = (control: ReportControl) => {
    const dataset = datasets.find((item) => item.id === control.dataset);
    const column = dataset?.columns.find((item) => item.key === control.field);
    return control.label ?? (column ? columnLabel(column) : control.field);
  };
  const shared = new Set(
    controls
      .map(named)
      .filter((label, index, all) => all.indexOf(label) !== index),
  );
  return (
    <Stack
      direction="row"
      align="center"
      gap={2}
      className="w-full flex-wrap gap-y-2 pb-3"
    >
      {before}
      {controls.map((control) => {
        const dataset = datasets.find(
          (dataset) => dataset.id === control.dataset,
        )!;
        const column = dataset.columns.find(
          (column) => column.key === control.field,
        )!;
        const label = shared.has(named(control))
          ? `${named(control)} · ${dataset.object?.plural ?? dataset.title ?? dataset.id}`
          : named(control);
        const value = values[control.id];
        if (control.type === "group")
          return (
            <FilterChip
              key={control.id}
              label={`Group by ${label}`}
              active={value === true}
              aria-label={`Group by ${label}`}
              aria-pressed={value === true}
              icon={
                value === true ? (
                  <CheckIcon className="size-3.5 shrink-0 text-brand" />
                ) : null
              }
              onClick={() =>
                setValue(control.id, value === true ? undefined : true)
              }
            />
          );
        if (control.operator === "in")
          return (
            <MultiSelect
              key={control.id}
              label={label}
              variant="filter"
              placeholder="All values"
              options={choices[control.id].map((item, index) => ({
                id: String(index),
                name: formatReportValue(item, column, currency),
              }))}
              value={(Array.isArray(value) ? value : []).flatMap((item) => {
                const index = choiceIndex(control.id, item);
                return index === undefined ? [] : [index];
              })}
              onChange={(indices) =>
                setValue(
                  control.id,
                  indices.length
                    ? indices.map((index) => choices[control.id][Number(index)])
                    : undefined,
                )
              }
            />
          );
        if (control.operator === "eq")
          return (
            <FilterChoice
              key={control.id}
              label={label}
              options={choices[control.id].map((item, index) => ({
                id: String(index),
                name: formatReportValue(item, column, currency),
              }))}
              value={
                value === undefined
                  ? undefined
                  : choiceIndex(control.id, value as ReportValue)
              }
              onChange={(index) =>
                setValue(
                  control.id,
                  index === undefined
                    ? undefined
                    : choices[control.id][Number(index)],
                )
              }
            />
          );
        if (column.type === "boolean")
          return (
            <FilterChoice
              key={control.id}
              label={label}
              options={BOOLEAN_CHOICES}
              value={value === undefined ? undefined : String(value)}
              onChange={(selected) =>
                setValue(
                  control.id,
                  selected === undefined ? undefined : selected === "true",
                )
              }
            />
          );
        const numeric = ["number", "currency", "percent"].includes(column.type);
        return (
          <FilterValue
            key={control.id}
            label={label}
            comparison={COMPARISON[control.operator as keyof typeof COMPARISON]}
            type={INPUT_TYPE[column.type]}
            display={
              value === undefined
                ? undefined
                : formatReportValue(value as ReportValue, column, currency)
            }
            value={
              value === undefined
                ? ""
                : column.type === "percent"
                  ? String(Number(value) * 100)
                  : String(value)
            }
            onChange={(typed) => {
              if (typed === "") return setValue(control.id, undefined);
              if (!numeric) return setValue(control.id, typed);
              // A number input holds things that are not numbers yet, and
              // "1e999" parses to Infinity, which JSON writes as null: the
              // request would then carry a bound of null rather than no bound.
              const parsed =
                Number(typed) / (column.type === "percent" ? 100 : 1);
              return setValue(
                control.id,
                Number.isFinite(parsed) ? parsed : undefined,
              );
            }}
          />
        );
      })}
      {!!Object.keys(values).length && (
        <Button
          variant="ghost"
          className="ml-auto"
          onClick={() => onChange({})}
        >
          Reset view
        </Button>
      )}
    </Stack>
  );
};
